import {Button, Group, Modal, Select, Stack, TextInput} from '@mantine/core';
import {useEffect, useState} from 'react';
import {Registration, RegistrationService, Update} from '../../client';
import {notifications} from '@mantine/notifications';

const FOOD_OPTIONS = [
    {value: 'vegan', label: 'Vegan'},
    {value: 'vegetarian', label: 'Vegetarian'},
    {value: 'mixed', label: 'Mixed'},
];

const TSHIRT_SIZES = ['x', 'xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl'];

// Women's sizes are stored with a "w-" prefix on the public registration form.
const TSHIRT_OPTIONS = [
    ...TSHIRT_SIZES.map((size) => ({value: size, label: `M - ${size.toUpperCase()}`})),
    ...TSHIRT_SIZES.map((size) => ({value: `w-${size}`, label: `W - ${size.toUpperCase()}`})),
];

type RegistrationEditModalProps = {
    registration: Registration | null;
    opened: boolean;
    onClose: () => void;
    onSaved: () => void;
};

export const RegistrationEditModal = ({
    registration,
    opened,
    onClose,
    onSaved,
}: RegistrationEditModalProps) => {
    const [email, setEmail] = useState('');
    const [food, setFood] = useState<string | null>(null);
    const [tshirt, setTshirt] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!registration) {
            return;
        }
        setEmail(registration.email);
        setFood(registration.food);
        setTshirt(registration.tshirt);
    }, [registration]);

    const handleSubmit = () => {
        if (!registration || !food || !tshirt || !email.trim()) {
            return;
        }

        const requestBody: Update = {
            email: email.trim(),
            food: food as Update['food'],
            tshirt: tshirt as Update['tshirt'],
        };

        setSaving(true);
        RegistrationService.updateRegistrationByIdApiV1RegistrationRegIdPut({
            regId: registration.id,
            requestBody,
        })
            .then(() => {
                onSaved();
                onClose();
            })
            .catch((error: { body?: { detail?: string }; message?: string }) => {
                notifications.show({
                    color: 'red',
                    title: 'Could not update registration',
                    message: error.body?.detail ?? error.message ?? 'Request failed',
                });
            })
            .finally(() => setSaving(false));
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title="Edit registration"
            centered
        >
            <Stack gap="md">
                <TextInput
                    label="Email"
                    required
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.currentTarget.value)}
                />
                <Select
                    label="Food"
                    required
                    data={FOOD_OPTIONS}
                    value={food}
                    onChange={setFood}
                    allowDeselect={false}
                />
                <Select
                    label="T-Shirt"
                    required
                    data={TSHIRT_OPTIONS}
                    value={tshirt}
                    onChange={setTshirt}
                    allowDeselect={false}
                />
                <Group justify="flex-end">
                    <Button variant="default" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} loading={saving}>
                        Save
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};
